"use client";

import React, { useEffect, useRef, useState } from "react";
import { Mail, Clock, Globe, MessageCircle } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
export default function ContactInformation() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const contactDetails = [{
    icon: Mail,
    title: "官方邮箱",
    value: "support@tlingtong.com",
    isLink: true,
    href: "mailto:support@tlingtong.com"
  }, {
    icon: Clock,
    title: "服务时间",
    value: "全天候服务，随时支持",
    isLink: false
  }, {
    icon: Globe,
    title: "官方网站",
    value: "www.tlingtong.com",
    isLink: true,
    href: "https://www.tlingtong.com"
  }];
  return <section id="联系信息" className="bg-background py-16 md:py-24 overflow-hidden">
      <div className="container mx-auto px-4 max-w-5xl">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 tracking-tight">
            联系我们
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            遇到问题或有任何建议？随时通过以下方式与我们取得联系，听力通团队竭诚为您服务。
          </p>
        </div>

        <div ref={ref} className={`grid grid-cols-1 md:grid-cols-2 gap-8 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {}
          <Card className="border-2 border-border/80 shadow-[0_8px_0_0_var(--color-border)] rounded-[24px] h-full flex flex-col transition-transform hover:-translate-y-1 duration-300 bg-card">
            <CardHeader className="pb-4">
              <CardTitle className="text-2xl font-bold flex items-center gap-2">
                <MessageCircle className="size-6 text-secondary" />
                沟通与支持
              </CardTitle>
              <CardDescription className="text-base">
                我们期待听到您的声音，帮助您更好地提升英语听力。
              </CardDescription>
            </CardHeader>
            <CardContent className="flex-1 flex flex-col justify-center gap-6 pt-4">
              {contactDetails.map((item, i) => <div data-index={i} key={i} className="flex items-start gap-4">
                  <div className="bg-secondary/10 p-3 rounded-2xl shrink-0">
                    <item.icon className="size-6 text-secondary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">
                      {item.title}
                    </h3>
                    {item.isLink && item.href ? <Link to={item.href} newTab={item.title === "官方网站"} className="text-muted-foreground hover:text-secondary transition-colors font-medium">
                        {item.value}
                      </Link> : <p className="text-muted-foreground font-medium">
                        {item.value}
                      </p>}
                  </div>
                </div>)}
            </CardContent>
          </Card>

          {}
          <Card className="border-2 border-border/80 shadow-[0_8px_0_0_var(--color-border)] rounded-[24px] h-full flex flex-col transition-transform hover:-translate-y-1 duration-300 bg-card text-center items-center justify-center p-8">
            <CardHeader className="pb-2">
              <CardTitle className="text-2xl font-bold">加入微信交流群</CardTitle>
              <CardDescription className="text-base">
                扫码加入官方社群，与更多学习者交流心得
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col items-center justify-center pt-6 w-full">
              <div className="relative p-4 bg-white rounded-3xl border-2 border-border/50 shadow-sm inline-block">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1618423/img/wechat_qr_community.png" alt="听力通微信交流群二维码" className="size-48 md:size-56 object-cover rounded-xl" />
                {}
                <div className="absolute -top-2 -left-2 size-4 border-t-2 border-l-2 border-secondary rounded-tl-lg"></div>
                <div className="absolute -top-2 -right-2 size-4 border-t-2 border-r-2 border-secondary rounded-tr-lg"></div>
                <div className="absolute -bottom-2 -left-2 size-4 border-b-2 border-l-2 border-secondary rounded-bl-lg"></div>
                <div className="absolute -bottom-2 -right-2 size-4 border-b-2 border-r-2 border-secondary rounded-br-lg"></div>
              </div>
              <p className="mt-6 text-sm font-medium text-secondary bg-secondary/10 px-4 py-2 rounded-full">
                获取最新学习资料与产品动态
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>;
}